/**
 * The consent builder's section list, as the page shows it.
 *
 * Since 2026-10-01 the server stores the study's own list on the builder
 * state (`sectionOrder`), because the privacy sections follow the study's
 * countries: the HIPAA authorization only for a US study, a data-protection
 * section for each other jurisdiction, or a "to be confirmed" placeholder.
 * A builder state written before then has no stored list and keeps the list
 * it was set up from, so open consent forms do not move.
 *
 * Pure, no React, so it is tested from functions' vitest (vitest.web.config.ts).
 */
import { privacyPlanKey } from './privacyPlanKey';

export interface ConsentSectionEntry {
  id: string;
  title: string;
}

/** The short labels the page shows. Ids not listed keep the server's title. */
const SHORT_LABELS: Record<string, string> = {
  summary_info: 'Summary',
  purpose: 'Purpose',
  participants_duration: 'Participants & Duration',
  procedures: 'Procedures',
  risks: 'Risks',
  benefits: 'Benefits',
  alternatives: 'Alternatives',
  confidentiality: 'Confidentiality',
  use_of_info: 'Use of Info',
  compensation: 'Compensation',
  medical_treatment: 'Medical Treatment',
  right_to_withdraw: 'Right to Withdraw',
  contacts: 'Contacts',
  hipaa: 'HIPAA Authorization',
  injury: 'Injury Compensation',
  clinicaltrials_gov: 'ClinicalTrials.gov',
  investigator_termination: 'Investigator Termination',
  new_findings: 'New Findings',
  signature_block: 'Signatures',
  gdpr: 'Data Protection (EU GDPR)',
  uk_gdpr: 'Data Protection (UK GDPR)',
  au_privacy: 'Privacy (Australia)',
  ca_privacy: 'Privacy (Canada)',
  privacy_tbd: 'Privacy (to be confirmed)',
  trial_registration: 'Trial Registration',
};

/** The list a builder state written before 2026-10-01 was set up from. */
const LEGACY_IDS = [
  'summary_info', 'purpose', 'participants_duration', 'procedures', 'risks', 'benefits',
  'alternatives', 'confidentiality', 'use_of_info', 'compensation', 'medical_treatment',
  'right_to_withdraw', 'contacts', 'hipaa', 'injury', 'clinicaltrials_gov',
  'investigator_termination', 'new_findings', 'signature_block',
];

const LEGACY_ORDER: ConsentSectionEntry[] = LEGACY_IDS.map((id) => ({ id, title: SHORT_LABELS[id] }));

export function consentSectionOrder(builderState: { sectionOrder?: unknown } | null | undefined): ConsentSectionEntry[] {
  const stored = builderState?.sectionOrder;
  if (
    Array.isArray(stored) &&
    stored.length > 0 &&
    stored.every((s) => s && typeof s.id === 'string' && typeof s.title === 'string')
  ) {
    return stored.map((s: ConsentSectionEntry) => ({ id: s.id, title: SHORT_LABELS[s.id] ?? s.title }));
  }
  return LEGACY_ORDER;
}

/** Shown on the consent builder page when the countries changed after setup. */
export const PRIVACY_NOTICE_TEXT =
  'The study countries have changed since this form was set up. The privacy sections may no longer match.';

/** Asked before a redraft started from the builder, with Cancel and Redraft. */
export const REDRAFT_CONFIRM_TEXT =
  'Redraft starts the consent form again for the current study countries. The current drafts are saved as a version.';

/**
 * Whether the page should show PRIVACY_NOTICE_TEXT. The server flags a form
 * whose countries changed after text was written (`privacyMismatch`); the page
 * also compares the PRIVACY PLAN the form was set up for with the plan for the
 * study's countries now (privacyPlanKey, the same classification the server
 * uses), so DE to FR, or adding FR to DE, which give the same sections, show
 * nothing. An untouched form is set up again by the server on its next read,
 * which clears both. A legacy form records no countries, so only the server's
 * flag can show it.
 */
export function privacyNoticeNeeded(
  builderState: { privacyMismatch?: unknown; countriesAtSetup?: unknown } | null | undefined,
  currentCountries: unknown,
): boolean {
  if (builderState?.privacyMismatch === true) return true;
  const atSetup = builderState?.countriesAtSetup;
  if (!Array.isArray(atSetup)) return false;
  return privacyPlanKey(atSetup) !== privacyPlanKey(currentCountries);
}
